import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import {
  Newspaper,
  Calendar,
  FileText,
  Download,
  Eye,
  Printer
} from "lucide-react";
import { Card, CardContent } from "../components/ui/card";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const newsletterFileUrl = (id) => `${API_URL}/api/newsletters/${id}/file`;

const newsletterDate = (n) => n?.data_newsletter || n?.data || null;

// Rileva se siamo su dispositivo mobile/tablet
const isMobileDevice = () => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
    (navigator.maxTouchPoints && navigator.maxTouchPoints > 2);
};

export default function Newsletter() {
  const { token } = useAuth();
  const [newsletters, setNewsletters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dataSource, setDataSource] = useState("loading");
  const [selectedNewsletter, setSelectedNewsletter] = useState(null);
  const [isMobile, setIsMobile] = useState(false);

  // Rileva mobile al mount
  useEffect(() => {
    setIsMobile(isMobileDevice());
  }, []);

  const fetchNewsletters = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/newsletters`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const list = response.data?.data || [];
      setNewsletters(list);
      setDataSource(response.data?.source || "mongo");
    } catch (error) {
      console.error("Error fetching newsletters:", error);
      setDataSource("error");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchNewsletters();
  }, [fetchNewsletters]);

  // Auto-refresh ogni 5 minuti per nuove newsletter
  useEffect(() => {
    const interval = setInterval(() => {
      fetchNewsletters();
    }, 5 * 60 * 1000); // 5 minuti
    
    return () => clearInterval(interval);
  }, [fetchNewsletters]);

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    try {
      return new Date(dateStr).toLocaleDateString('it-IT', {
        day: '2-digit',
        month: 'long',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  const handleOpenNewsletter = (newsletter) => {
    setSelectedNewsletter(newsletter);
  };

  if (loading && newsletters.length === 0) {
    return (
      <div className="p-4 md:p-6 lg:p-8 max-w-6xl mx-auto">
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-purple-200 border-t-purple-600 rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 lg:p-8 max-w-6xl mx-auto">
      {/* Page Header - responsive */}
      <PageHeader
        title="Newsletter"
        subtitle="Comunicazioni e aggiornamenti per le strutture"
        icon={Newspaper}
        iconContainerClassName="bg-gradient-to-br from-purple-500 to-purple-600"
        iconClassName="text-white"
      />

      {/* Stats */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        <Card className="card-base" data-testid="stat-total">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-purple-600">{newsletters.length}</p>
            <p className="text-sm text-slate-500">Newsletter pubblicate</p>
          </CardContent>
        </Card>
        <Card className="card-base" data-testid="stat-recent">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-slate-600">
              {newsletters.filter(n => {
                const d = newsletterDate(n);
                if (!d) return false;
                const newsDate = new Date(d);
                const thirtyDaysAgo = new Date();
                thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
                return newsDate >= thirtyDaysAgo;
              }).length}
            </p>
            <p className="text-sm text-slate-500">Ultimi 30 giorni</p>
          </CardContent>
        </Card>
      </div>

      {/* Newsletter List */}
      <div className="space-y-4" data-testid="newsletters-list">
        {newsletters.map((newsletter) => (
          <Card
            key={newsletter.id}
            className="card-base cursor-pointer hover:shadow-lg transition-all hover:border-purple-200"
            onClick={() => handleOpenNewsletter(newsletter)}
            data-testid={`newsletter-${newsletter.id}`}
          >
            <CardContent className="p-5">
              <div className="flex items-center gap-4">
                {/* Icon */}
                <div className="w-14 h-14 rounded-xl bg-purple-100 flex items-center justify-center flex-shrink-0">
                  <FileText className="w-7 h-7 text-purple-600" />
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg font-bold text-slate-900 truncate">
                    {newsletter.nome || "Newsletter senza nome"}
                  </h3>
                  <div className="flex items-center gap-2 mt-1 text-sm text-slate-500">
                    <Calendar className="w-4 h-4" />
                    <span>{formatDate(newsletterDate(newsletter))}</span>
                  </div>
                </div>

                {/* Action */}
                <div className="flex items-center gap-2">
                  {(newsletter.has_file || newsletter.file_name) && (
                    <Badge className="bg-red-100 text-red-700">
                      PDF
                    </Badge>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-purple-600 hover:text-purple-700 hover:bg-purple-50"
                  >
                    <Eye className="w-5 h-5" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {newsletters.length === 0 && (
        <Card className="card-base">
          <CardContent className="p-12 text-center">
            <Newspaper className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">Nessuna newsletter disponibile</p>
            <p className="text-sm text-slate-400 mt-1">
              Le newsletter verranno visualizzate qui quando saranno pubblicate
            </p>
          </CardContent>
        </Card>
      )}

      {/* Newsletter Detail Modal */}
      <Dialog open={!!selectedNewsletter} onOpenChange={() => setSelectedNewsletter(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col">
          {selectedNewsletter && (
            <>
              <DialogHeader className="flex-shrink-0">
                <DialogTitle className="text-2xl font-bold text-slate-900 pr-8">
                  {selectedNewsletter.nome}
                </DialogTitle>
                <div className="flex items-center gap-2 text-slate-500 mt-2">
                  <Calendar className="w-4 h-4" />
                  <span>{formatDate(newsletterDate(selectedNewsletter))}</span>
                </div>
              </DialogHeader>

              <div className="flex-1 overflow-hidden mt-4">
                {/* File Viewer (PDF o Immagine) */}
                {(selectedNewsletter.has_file || selectedNewsletter.file_name || selectedNewsletter.file_id) ? (
                  <div className="h-full flex flex-col">
                    {/* Toolbar */}
                    <div className="flex justify-end gap-2 mb-3 flex-shrink-0">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          const printWindow = window.open(
                            newsletterFileUrl(selectedNewsletter.id),
                            '_blank'
                          );
                          if (printWindow) {
                            printWindow.onload = () => {
                              setTimeout(() => printWindow.print(), 500);
                            };
                          }
                        }}
                      >
                        <Printer className="w-4 h-4 mr-2" />
                        Stampa
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => window.open(
                          newsletterFileUrl(selectedNewsletter.id),
                          '_blank'
                        )}
                      >
                        <Eye className="w-4 h-4 mr-2" />
                        Apri in nuova scheda
                      </Button>
                    </div>
                    {/* Content viewer - scrollable */}
                    <div className="flex-1 rounded-xl overflow-hidden border bg-slate-50" style={{ minHeight: '400px', maxHeight: '60vh' }}>
                      {isMobile ? (
                        /* Su mobile mostra direttamente i pulsanti */
                        <div className="flex flex-col items-center justify-center h-full p-8 text-center" style={{ minHeight: '400px' }}>
                          <FileText className="w-20 h-20 text-purple-400 mb-6" />
                          <h3 className="text-lg font-semibold text-slate-700 mb-2">
                            {selectedNewsletter.nome}
                          </h3>
                          <p className="text-slate-500 mb-6">
                            Il documento PDF è pronto per la visualizzazione
                          </p>
                          <div className="flex flex-col sm:flex-row gap-3 w-full max-w-xs">
                            <a
                              href={newsletterFileUrl(selectedNewsletter.id)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex-1"
                            >
                              <Button
                                className="w-full bg-purple-600 hover:bg-purple-700"
                                size="lg"
                              >
                                <Eye className="w-5 h-5 mr-2" />
                                Apri PDF
                              </Button>
                            </a>
                            <a
                              href={newsletterFileUrl(selectedNewsletter.id)}
                              download={`${selectedNewsletter.nome}.pdf`}
                              className="flex-1"
                            >
                              <Button
                                variant="outline"
                                size="lg"
                                className="w-full"
                              >
                                <Download className="w-5 h-5 mr-2" />
                                Scarica
                              </Button>
                            </a>
                          </div>
                        </div>
                      ) : (
                        /* Su desktop prova con object/embed */
                        <object
                          data={`${newsletterFileUrl(selectedNewsletter.id)}#toolbar=1&navpanes=0`}
                          type="application/pdf"
                          className="w-full h-full"
                          style={{ minHeight: '400px', height: '60vh' }}
                        >
                          {/* Fallback per browser senza supporto PDF */}
                          <div className="flex flex-col items-center justify-center h-full p-8 text-center">
                            <FileText className="w-16 h-16 text-purple-300 mb-4" />
                            <p className="text-slate-600 mb-4">
                              Il documento PDF è pronto per la visualizzazione.
                            </p>
                            <div className="flex gap-3">
                              <Button
                                onClick={() => window.open(newsletterFileUrl(selectedNewsletter.id), '_blank')}
                                className="bg-purple-600 hover:bg-purple-700"
                              >
                                <Eye className="w-4 h-4 mr-2" />
                                Apri PDF
                              </Button>
                              <Button
                                variant="outline"
                                onClick={() => {
                                  const link = document.createElement('a');
                                  link.href = newsletterFileUrl(selectedNewsletter.id);
                                  link.download = `${selectedNewsletter.nome}.pdf`;
                                  link.click();
                                }}
                              >
                                <Download className="w-4 h-4 mr-2" />
                                Scarica
                              </Button>
                            </div>
                          </div>
                        </object>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="border-2 border-dashed border-slate-200 rounded-xl p-8 text-center">
                    <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500">
                      Nessun documento allegato a questa newsletter
                    </p>
                  </div>
                )}
              </div>

              <div className="flex justify-end mt-4 flex-shrink-0">
                <Button variant="outline" onClick={() => setSelectedNewsletter(null)}>
                  Chiudi
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
